// Assessment Heatmap - Visual HMI for performance analytics
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, Zap, Brain } from 'lucide-react';

const HeatmapInsights = ({ 
  performanceData = {},
  categoryMastery = {},
  engagementScore = 0,
  className = '' 
}) => {
  // Generate heatmap data
  const generateHeatmapData = () => {
    const difficulties = ['easy', 'medium', 'hard'];
    const categories = Object.keys(categoryMastery);
    
    return difficulties.map(difficulty => ({
      difficulty,
      performance: performanceData[difficulty] || 0,
      color: getHeatmapColor(performanceData[difficulty] || 0)
    }));
  };

  const getHeatmapColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getEngagementColor = () => {
    if (engagementScore >= 80) return 'from-emerald-400 to-teal-500';
    if (engagementScore >= 60) return 'from-blue-400 to-indigo-500';
    if (engagementScore >= 40) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-pink-500';
  };

  const heatmapData = generateHeatmapData();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-lg border border-slate-200 p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Performance Heatmap</h3>
        <div className="flex items-center space-x-2">
          <Brain className="w-5 h-5 text-indigo-600" />
          <span className="text-sm text-gray-600">AI Analytics</span>
        </div>
      </div>

      {/* Performance vs Difficulty Grid */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Performance vs Difficulty</h4>
        <div className="grid grid-cols-3 gap-2">
          {heatmapData.map((item, index) => (
            <motion.div
              key={item.difficulty}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              <div className={`h-16 rounded-lg ${item.color} flex items-center justify-center text-white font-medium text-sm relative overflow-hidden`}>
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ 
                    duration: 1.5, 
                    delay: index * 0.2,
                    ease: "easeInOut"
                  }}
                />
                <div className="relative z-10">
                  <div className="capitalize">{item.difficulty}</div>
                  <div className="text-xs opacity-90">{item.performance}%</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Category Mastery Distribution */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Category Mastery</h4>
        <div className="space-y-2">
          {Object.entries(categoryMastery).map(([category, mastery], index) => (
            <motion.div
              key={category}
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className="flex items-center space-x-3"
            >
              <span className="text-xs font-medium text-gray-600 w-20 truncate">
                {category}
              </span>
              <div className="flex-1 bg-gray-200 rounded-full h-2 relative overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${getHeatmapColor(mastery)}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${mastery}%` }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.8 }}
                />
              </div>
              <span className="text-xs font-medium text-gray-700 w-8">
                {Math.round(mastery)}%
              </span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Engagement Score */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Engagement Score</h4>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="relative"
        >
          <div className={`h-20 rounded-xl bg-gradient-to-r ${getEngagementColor()} flex items-center justify-center text-white relative overflow-hidden`}>
            <motion.div
              className="absolute inset-0 bg-white/10"
              animate={{
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <div className="relative z-10 text-center">
              <div className="text-2xl font-bold">{Math.round(engagementScore)}%</div>
              <div className="text-sm opacity-90 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                Engagement
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-emerald-500 rounded"></div>
              <span>Excellent</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Good</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-orange-500 rounded"></div>
              <span>Fair</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Needs Work</span>
            </div>
          </div>
          <div className="text-xs text-gray-400">Real-time Analytics</div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeatmapInsights;